import {
  IMAGE_MANIFEST,
  ImageSources,
  ImageKey,
} from "@/images/generated/manifest";

interface ProjectDemo {
  /** Public URL that is framable (no X-Frame-Options / CSP frame-ancestors) and auth-free. */
  url: string;
  /** Device chrome to render around the iframe. */
  frame: "phone" | "browser";
}

/** How the live product is shown: desktop browser + overlapping phone, or one phone. */
export type ProjectDevice = "duo" | "phone";

export interface ProjectBrand {
  /** The product's own brand color — background of its spine/band and hero. */
  bg: string;
  /** Text color with sufficient contrast on `bg`. */
  ink: string;
}

export interface ProjectScreens {
  /** 1280×800 top crop of the live site at a 1440×900 viewport. Absent for phone-only projects. */
  desktop?: ImageSources;
  /** 390×844 capture of the live site on a phone viewport. */
  mobile: ImageSources;
}

export interface Project {
  // Keyed to the generated manifest so a renamed/missing image is a compile error.
  key: ImageKey;
  slug: string;
  name: string;
  /** AI feature art — the master for the baked OG/social cards (public/og). */
  img: ImageSources;
  /** Real screenshots of the live product (portfolio spines, case-study hero). */
  screens: ProjectScreens;
  /** Riso illustration for the collapsed desktop spine (232×1300). */
  spine: ImageSources;
  brand: ProjectBrand;
  device: ProjectDevice;
  liveUrl: string;
  tags: readonly string[];
  featured: boolean;
  /** When present, enables the click-to-run live-demo embed on the case study page. */
  demo?: ProjectDemo;
}

export const PROJECTS: readonly Project[] = [
  {
    key: "vasanti",
    slug: "vasanti",
    name: "Vasantí",
    img: IMAGE_MANIFEST.vasanti,
    screens: { desktop: IMAGE_MANIFEST.vasantiDesktop, mobile: IMAGE_MANIFEST.vasantiMobile },
    spine: IMAGE_MANIFEST.vasantiSpine,
    brand: { bg: "#f2c6db", ink: "#3a0a1c" },
    device: "duo",
    liveUrl: "https://vasanticases.com",
    tags: ["Next.js", "NestJS", "Design editor", "Wompi"],
    featured: false,
  },
  {
    key: "patitas",
    slug: "patitas",
    name: "Patitas",
    img: IMAGE_MANIFEST.patitas,
    screens: { desktop: IMAGE_MANIFEST.patitasDesktop, mobile: IMAGE_MANIFEST.patitasMobile },
    spine: IMAGE_MANIFEST.patitasSpine,
    brand: { bg: "#00311d", ink: "#f4f6f8" },
    device: "duo",
    liveUrl: "https://patitascolombia.com",
    tags: ["PWA", "Wompi", "Shelter Ops", "Gemini"],
    featured: true,
    // No `demo`: patitascolombia.com sends X-Frame-Options: SAMEORIGIN, so it
    // cannot be embedded. The case study still links out to the live site.
  },
  {
    key: "atrium",
    slug: "atrium",
    name: "Atrium",
    img: IMAGE_MANIFEST.atrium,
    screens: { desktop: IMAGE_MANIFEST.atriumDesktop, mobile: IMAGE_MANIFEST.atriumMobile },
    spine: IMAGE_MANIFEST.atriumSpine,
    brand: { bg: "#3b3027", ink: "#f4ede2" },
    device: "duo",
    liveUrl: "https://atrium.devbloom.dev",
    tags: ["NestJS", "Prisma", "React", "Multi-tenant"],
    featured: false,
  },  {
    key: "staged",
    slug: "staged",
    name: "Staged",
    img: IMAGE_MANIFEST.staged,
    screens: { desktop: IMAGE_MANIFEST.stagedDesktop, mobile: IMAGE_MANIFEST.stagedMobile },
    spine: IMAGE_MANIFEST.stagedSpine,
    brand: { bg: "#15803d", ink: "#ffffff" },
    device: "duo",
    liveUrl: "https://staged.devbloom.dev",
    tags: ["iOS", "Expo", "Multipeer", "TestFlight"],
    featured: false,
  },
  {
    key: "bloomAssistant",
    slug: "bloom-assistant",
    name: "Bloom Assistant",
    img: IMAGE_MANIFEST.bloomAssistant,
    // TODO: only the public Google sign-in/onboarding screen is reachable without
    // an account — the owner will supply in-app screenshots.
    screens: { mobile: IMAGE_MANIFEST.bloomAssistantMobile },
    spine: IMAGE_MANIFEST.bloomAssistantSpine,
    brand: { bg: "#5b5bd6", ink: "#ffffff" },
    device: "phone",
    liveUrl: "https://chat.devbloom.dev",
    tags: ["PWA", "Claude AI", "Firebase", "Ionic React"],
    featured: false,
  },
  {
    key: "devflow",
    slug: "devflow",
    name: "DevFlow",
    img: IMAGE_MANIFEST.devflow,
    screens: { desktop: IMAGE_MANIFEST.devflowDesktop, mobile: IMAGE_MANIFEST.devflowMobile },
    spine: IMAGE_MANIFEST.devflowSpine,
    brand: { bg: "#f39a1e", ink: "#2a1600" },
    device: "duo",
    liveUrl: "https://devflow.devbloom.dev",
    tags: ["TypeScript", "Node.js", "Commander.js", "Open Source"],
    featured: false,
  },
  {
    key: "toller",
    slug: "toller",
    name: "Toller",
    img: IMAGE_MANIFEST.toller,
    screens: { desktop: IMAGE_MANIFEST.tollerDesktop, mobile: IMAGE_MANIFEST.tollerMobile },
    spine: IMAGE_MANIFEST.tollerSpine,
    brand: { bg: "#1d4ed8", ink: "#ffffff" },
    device: "duo",
    liveUrl: "https://toller.devbloom.dev",
    tags: ["Maps", "Realtime", "Geospatial"],
    featured: false,
    demo: { url: "https://toller.devbloom.dev", frame: "phone" },
  },
  {
    key: "spectra",
    slug: "spectra",
    name: "Spectra",
    img: IMAGE_MANIFEST.spectra,
    screens: { desktop: IMAGE_MANIFEST.spectraDesktop, mobile: IMAGE_MANIFEST.spectraMobile },
    spine: IMAGE_MANIFEST.spectraSpine,
    brand: { bg: "#0f172a", ink: "#f4f6f8" },
    device: "duo",
    liveUrl: "https://spectra.devbloom.dev",
    tags: ["Gemini Vision", "Creative AI"],
    featured: false,
    demo: { url: "https://spectra.devbloom.dev", frame: "browser" },
  },
  {
    key: "expenseTracker",
    slug: "expense-tracker",
    name: "Expense Tracker",
    img: IMAGE_MANIFEST.expenseTracker,
    // TODO: only the public Google sign-in/onboarding screen is reachable without
    // an account — the owner will supply in-app screenshots.
    screens: { mobile: IMAGE_MANIFEST.expenseTrackerMobile },
    spine: IMAGE_MANIFEST.expenseTrackerSpine,
    brand: { bg: "#4d6e37", ink: "#ffffff" },
    device: "phone",
    liveUrl: "https://expenses.devbloom.dev",
    tags: ["OCR", "PWA", "Budgets"],
    featured: false,
  },

] as const;

export function findProjectBySlug(slug: string): Project | undefined {
  return PROJECTS.find((p) => p.slug === slug);
}
